import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Collapse,
  Stack,
  Divider,
  alpha,
} from '@mui/material';
import { useSubscription } from './SubscriptionContext';
import LockIcon from '@mui/icons-material/Lock';
// LAP-733 (GE022): pull translations + active language from the shared
// helper so the filter panel matches the rest of the Smart Feed.
import { useSelector } from 'react-redux';
import { getTranslation, getUserLanguage } from './translations';

const FilterPanel = ({ filters, onFilterChange, publications, open = false }) => {
  const { subscription } = useSubscription();
  const siteUser = useSelector((store) => store.user);
  const lang = getUserLanguage(siteUser);
  const tr = (key, vars) => getTranslation(key, lang, vars);

  const domains = ['all', ...new Set(publications.map(p => p.domain))];
  const types = ['all', ...new Set(publications.map(p => p.type))];

  // Map a domain/type code to a translation key. Fallback to the original
  // label so unknown values still appear in chips rather than disappearing.
  const labelForDomain = (domain) => {
    const map = {
      all: 'all',
      Finance: 'finance',
      Social: 'social',
      Professional: 'professional',
      Personal: 'personal',
      Time: 'time',
      Habitation: 'home',
      General: 'roadmap',
      Planet: 'planet',
      Administrative: 'administrative',
    };
    const key = map[domain];
    return key ? tr(key) : domain;
  };
  const labelForType = (type) => {
    const map = {
      all: 'all',
      Actionable: 'actionable',
      Summary: 'summary',
      Reminder: 'reminder',
      Publication: 'publication',
      News: 'news',
    };
    const key = map[type];
    return key ? tr(key) : type;
  };

  const getDomainColor = (domain) => {
    const colors = {
      all: '#6366f1',
      Finance: '#10b981',
      Social: '#ec4899',
      Health: '#ef4444',
      Professional: '#3b82f6',
      Personal: '#8b5cf6',
      Time: '#f59e0b',
      Habitation: '#06b6d4',
      General: '#6366f1',
    };
    return colors[domain] || '#6366f1';
  };

  const getTypeColor = (type) => {
    const colors = {
      all: '#6366f1',
      Actionable: '#f59e0b',
      Summary: '#3b82f6',
      Reminder: '#ef4444',
      Publication: '#8b5cf6',
      Motivational: '#10b981',
      Custom: '#6366f1',
      News: '#06b6d4',
    };
    return colors[type] || '#6366f1';
  };

  const handleDomainChange = (domain) => {
    onFilterChange({ ...filters, domain });
  };

  const handleTypeChange = (type) => {
    onFilterChange({ ...filters, type });
  };

  const countByDomain = (domain) => {
    if (domain === 'all') return publications.length;
    return publications.filter(p => p.domain === domain).length;
  };

  const countByType = (type) => {
    if (type === 'all') return publications.length;
    return publications.filter(p => p.type === type).length;
  };

  return (
    <Collapse in={open} timeout="auto" unmountOnExit={false}>
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: 3,
          bgcolor: '#1a1d2e',
          borderRadius: '16px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Stack spacing={3}>
          {/* Subscription Status */}
          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                mb: 1,
                color: '#E0E0E0',
                fontWeight: 600,
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              {tr('subscription')}
            </Typography>
            <Chip
              label={subscription}
              sx={{
                bgcolor: subscription === 'PREMIUM' 
                  ? alpha('#8b5cf6', 0.2) 
                  : subscription === 'STANDARD'
                  ? alpha('#3b82f6', 0.2)
                  : alpha('#6366f1', 0.2),
                color: subscription === 'PREMIUM' 
                  ? '#8b5cf6' 
                  : subscription === 'STANDARD'
                  ? '#3b82f6'
                  : '#6366f1',
                fontWeight: 600,
                fontSize: '0.8rem',
              }}
            />
          </Box>

          <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.05)' }} />

          {/* Domain Filter */}
          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                mb: 2,
                color: '#E0E0E0',
                fontWeight: 600,
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              {tr('filterByDomain')}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {domains.map((domain) => {
                const isSelected = filters.domain === domain;
                const color = getDomainColor(domain);
                const count = countByDomain(domain);

                return (
                  <Chip
                    key={domain}
                    label={`${labelForDomain(domain)} (${count})`}
                    onClick={() => handleDomainChange(domain)}
                    sx={{
                      bgcolor: isSelected ? alpha(color, 0.2) : 'rgba(255, 255, 255, 0.05)',
                      color: isSelected ? color : 'rgba(255, 255, 255, 0.7)',
                      border: `1px solid ${isSelected ? color : 'rgba(255, 255, 255, 0.1)'}`,
                      fontWeight: isSelected ? 600 : 400,
                      fontSize: '0.8rem',
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: alpha(color, 0.15),
                        borderColor: color,
                        color: color,
                        transform: 'translateY(-1px)',
                      },
                    }}
                  />
                );
              })}
            </Stack>
          </Box>

          <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.05)' }} />

          {/* Type Filter */}
          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                mb: 2,
                color: '#E0E0E0',
                fontWeight: 600,
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              {tr('filterByType')}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {types.map((type) => {
                const isSelected = filters.type === type;
                const color = getTypeColor(type);
                const count = countByType(type);

                return (
                  <Chip
                    key={type}
                    label={`${labelForType(type)} (${count})`}
                    onClick={() => handleTypeChange(type)}
                    sx={{
                      bgcolor: isSelected ? alpha(color, 0.2) : 'rgba(255, 255, 255, 0.05)',
                      color: isSelected ? color : 'rgba(255, 255, 255, 0.7)',
                      border: `1px solid ${isSelected ? color : 'rgba(255, 255, 255, 0.1)'}`,
                      fontWeight: isSelected ? 600 : 400,
                      fontSize: '0.8rem',
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: alpha(color, 0.15),
                        borderColor: color,
                        color: color,
                        transform: 'translateY(-1px)',
                      },
                    }}
                  />
                );
              })}
            </Stack>
          </Box>
        </Stack>
      </Paper>
    </Collapse>
  );
};

export default FilterPanel;
